import * as THREE from 'three';
import '../ui/fortress.css';
import { C } from '../core/palette.js';
import { box, cyl, sphere, textSprite } from '../core/meshkit.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { Townsperson, checkNpcClick } from '../entities/townsfolk.js';
import { HUD } from '../ui/hud.js';
import { addCrumbs, setFlag, getFlag, getState } from '../core/save.js';
import { addFound, hasFound } from '../core/collection.js';
import { flagObjective, isActive, isCompleted, isReady, turnIn } from '../core/quests.js';

// The Baker King's Chamber — the innermost heart of the Fortress.
// Three floor symbols must be pressed in the correct order to rouse the king's
// spirit from the throne. Solving it rewards the royal scepter and a permanent
// town upgrade discount.

export function createFortressThroneScene(){
  return () => new FortressThroneScene();
}

const STONE   = 0x7C776C;
const STONE_D = 0x5E5A50;
const STONE_L = 0x928C7F;
const GOLD    = 0xE8C24B;
const BANNER1 = 0x9A2C46;
const BANNER2 = 0x2E479A;

// The three symbols, in the order the player must trigger them.
const SYMBOLS = [
  { id: 'crust',  name: 'Crust',  color: 0xB5824E, icon: '🥖', x: -2.2, z: -2 },
  { id: 'toast',  name: 'Toast',  color: 0xEFB96A, icon: '🍞', x: 0,    z: -3.4 },
  { id: 'jam',    name: 'Jam',    color: 0xE6537B, icon: '🍯', x: 2.2,  z: -2 }
];

class FortressThroneScene {
  constructor(){
    this.state = 'explore';
    this.panels = [];
    this.vignette = null;
    this.sequence = []; // player inputs so far
    this.solved = getFlag('kingsChamberSolved');

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x181018);
    this.scene.fog = new THREE.Fog(0x181018, 16, 46);

    this.camera = new THREE.PerspectiveCamera(52, window.innerWidth/window.innerHeight, 0.1, 100);
    this.camDist = 14;
    this.camTarget = new THREE.Vector3(0, 1.6, 0);
    this.azimuth = 0; this.polar = 0.5;

    this._buildLights();
    this._buildRoom();
    this._buildThrone();
    this._buildSymbols();
    this._buildBanners();

    this.bread = new BreadBoi();
    this.bread.root.position.set(0, 0, 5.5);
    this.bread.root.rotation.y = Math.PI;
    this.scene.add(this.bread.root);

    this.king = this._makeKing();
    this.king.root.visible = this.solved;
    this.king.root.position.set(0, 0, -7.5);
    this.king.root.rotation.y = 0;
    this.scene.add(this.king.root);

    this.altarPos = new THREE.Vector3(0, 0, -4.5);
    this._buildAltar();

    this.hotspots = [
      ...SYMBOLS.map((s, i) => ({
        pos: new THREE.Vector3(s.x, 0, s.z), r: 1.5,
        label: `${s.icon} press the ${s.name} symbol`,
        action: () => this._pressSymbol(i)
      })),
      { pos: this.altarPos, r: 2.0, label: '🍞 place offering at the altar', action: () => this._altarOffer() },
      { pos: this.king.root.position.clone(), r: 2.6, label: '🗣 speak to the Baker King', action: () => this._talkToKing() },
      { pos: new THREE.Vector3(0, 0, 7.4), r: 2.2, label: '← back to the Fortress hall', action: () => this._exit() }
    ];
    this.nearSpot = null;
  }

  _buildLights(){
    this.scene.add(new THREE.AmbientLight(0x403040, 0.7));
    const moon = new THREE.DirectionalLight(0x7A8AB0, 0.55);
    moon.position.set(4, 14, 8); moon.castShadow = true;
    moon.shadow.mapSize.set(2048, 2048);
    const s = 20;
    moon.shadow.camera.left = -s; moon.shadow.camera.right = s;
    moon.shadow.camera.top = s; moon.shadow.camera.bottom = -s;
    this.scene.add(moon);

    // throne spotlight
    this.throneLight = new THREE.SpotLight(GOLD, 0, 18, Math.PI/5, 0.5, 1);
    this.throneLight.position.set(0, 12, -2);
    this.throneLight.target.position.set(0, 0, -7);
    this.scene.add(this.throneLight);
    this.scene.add(this.throneLight.target);
  }

  _buildRoom(){
    const HALF_W = 8, HALF_D = 9;
    // floor
    const floor = new THREE.Mesh(new THREE.PlaneGeometry(HALF_W * 2, HALF_D * 2), new THREE.MeshLambertMaterial({ color: STONE }));
    floor.rotation.x = -Math.PI/2; floor.receiveShadow = true; this.scene.add(floor);
    // central carpet
    const carpet = new THREE.Mesh(new THREE.PlaneGeometry(3.2, 12), new THREE.MeshLambertMaterial({ color: BANNER1 }));
    carpet.rotation.x = -Math.PI/2; carpet.position.set(0, 0.02, -1); this.scene.add(carpet);
    // walls
    const wallMat = new THREE.MeshLambertMaterial({ color: STONE_D });
    [-HALF_W, HALF_W].forEach(x => {
      const w = new THREE.Mesh(new THREE.PlaneGeometry(HALF_D * 2, 9), wallMat);
      w.rotation.y = x < 0 ? Math.PI/2 : -Math.PI/2; w.position.set(x, 4.5, 0); w.receiveShadow = true;
      this.scene.add(w);
    });
    const back = new THREE.Mesh(new THREE.PlaneGeometry(HALF_W * 2, 9), wallMat);
    back.position.set(0, 4.5, -HALF_D); back.receiveShadow = true; this.scene.add(back);
    // entrance arch
    box(HALF_W * 2, 9, 0.4, STONE_D, 0, 4.5, HALF_D, this.scene);
    const archW = 2.8;
    box((HALF_W * 2 - archW) / 2, 9, 0.5, STONE_D, -(archW/2 + (HALF_W * 2 - archW)/4), 4.5, HALF_D, this.scene);
    box((HALF_W * 2 - archW) / 2, 9, 0.5, STONE_D,  (archW/2 + (HALF_W * 2 - archW)/4), 4.5, HALF_D, this.scene);
    box(archW + 0.2, 2.6, 0.5, STONE, 0, 7.2, HALF_D, this.scene);
    // ceiling
    const ceil = new THREE.Mesh(new THREE.PlaneGeometry(HALF_W * 2, HALF_D * 2), new THREE.MeshLambertMaterial({ color: 0x140E14 }));
    ceil.rotation.x = Math.PI/2; ceil.position.set(0, 9, 0); this.scene.add(ceil);
    // sign
    const sign = textSprite('The King\'s Chamber', 28, '#EFE6D0', 4.6, 0.9);
    sign.position.set(0, 7.0, -HALF_D + 0.4); this.scene.add(sign);
    const exitSign = textSprite('← to Fortress', 22, '#C9B98F', 2.6, 0.7);
    exitSign.position.set(0, 5.2, HALF_D - 0.3); this.scene.add(exitSign);
  }

  _buildThrone(){
    const g = new THREE.Group(); g.position.set(0, 0, -7.5); this.scene.add(g);
    // dais
    box(4.2, 0.6, 3.2, STONE_L, 0, 0.3, 0, g);
    // throne seat/back
    box(2.2, 0.5, 1.0, 0x5A3A22, 0, 0.85, 0, g);
    box(2.2, 2.8, 0.35, 0x5A3A22, 0, 2.0, -0.35, g);
    // gold trim
    box(2.35, 0.12, 1.15, GOLD, 0, 1.12, 0, g);
    box(2.35, 2.9, 0.12, GOLD, 0, 2.0, -0.52, g);
    // arm rests
    box(0.25, 0.2, 1.0, GOLD, -1.0, 1.1, 0.1, g);
    box(0.25, 0.2, 1.0, GOLD,  1.0, 1.1, 0.1, g);
    // dormant golem king slumped on throne
    this.kingBody = new THREE.Group(); g.add(this.kingBody);
    const body = sphere(0.55, 0x6E5A50, 0, 1.6, 0, this.kingBody, 16);
    body.scale.set(1.1, 1, 1);
    cyl(0.18, 0.2, 0.65, 0x6E5A50, -0.5, 1.4, 0.2, this.kingBody, 8).rotation.z = 0.6;
    cyl(0.18, 0.2, 0.65, 0x6E5A50, 0.5, 1.4, 0.2, this.kingBody, 8).rotation.z = -0.6;
    // crown
    const crown = new THREE.Group(); crown.position.set(0, 2.25, 0); this.kingBody.add(crown);
    cyl(0.32, 0.34, 0.18, GOLD, 0, 0, 0, crown, 14);
    for (let i = 0; i < 5; i++){
      const a = (i - 2) * 0.35;
      const p = new THREE.Mesh(new THREE.ConeGeometry(0.06, 0.22, 6), new THREE.MeshBasicMaterial({ color: GOLD }));
      p.position.set(Math.sin(a) * 0.28, 0.18, Math.cos(a) * 0.28); crown.add(p);
    }
    this.kingBody.rotation.x = 0.25; // slumped
  }

  _buildSymbols(){
    this.symbolMeshes = [];
    SYMBOLS.forEach((s, i) => {
      const g = new THREE.Group(); g.position.set(s.x, 0, s.z); this.scene.add(g);
      // unlit base tile
      const base = box(1.4, 0.08, 1.4, 0x3A3A3A, 0, 0.05, 0, g);
      base.receiveShadow = true;
      // glowing inset (initially dim)
      const inset = box(1.1, 0.06, 1.1, s.color, 0, 0.1, 0, g);
      inset.material = new THREE.MeshBasicMaterial({ color: s.color, transparent: true, opacity: 0.25 });
      // icon sprite
      const icon = textSprite(s.icon, 50, '#FFF4DF', 0.9, 0.9);
      icon.position.set(0, 0.14, 0); g.add(icon);
      this.symbolMeshes.push({ base, inset, lit: false });
    });
  }

  _buildBanners(){
    [-6.5, 6.5].forEach((x, side) => {
      [-5, 2].forEach((z, i) => {
        const col = (side + i) % 2 ? BANNER2 : BANNER1;
        const b = box(1.5, 3.6, 0.08, col, x, 5.0, z, this.scene);
        b.rotation.y = x < 0 ? Math.PI/2 : -Math.PI/2;
      });
    });
  }

  _makeKing(){
    const SPIRIT = 0xE8C24B;
    const build = (g) => {
      // glowing monarch silhouette
      const body = sphere(0.36, SPIRIT, 0, 1.05, 0, g, 14);
      body.material.transparent = true; body.material.opacity = 0.78;
      box(0.7, 0.8, 0.35, SPIRIT, 0, 0.55, 0, g).material.transparent = true;
      sphere(0.06, 0xFFF4DF, -0.14, 1.15, 0.24, g, 8);
      sphere(0.06, 0xFFF4DF,  0.14, 1.15, 0.24, g, 8);
      // crown
      const crown = new THREE.Group(); crown.position.set(0, 1.55, 0); g.add(crown);
      cyl(0.28, 0.3, 0.16, GOLD, 0, 0, 0, crown, 12);
      for (let i = 0; i < 5; i++){
        const a = (i - 2) * 0.35;
        const p = new THREE.Mesh(new THREE.ConeGeometry(0.05, 0.18, 6), new THREE.MeshBasicMaterial({ color: GOLD }));
        p.position.set(Math.sin(a) * 0.24, 0.16, Math.cos(a) * 0.24); crown.add(p);
      }
      // scepter
      cyl(0.03, 0.03, 1.6, GOLD, 0.45, 0.9, 0.25, g, 6).rotation.z = -0.2;
      sphere(0.1, GOLD, 0.58, 1.7, 0.38, g, 10);
    };
    return new Townsperson({
      name: 'Baker King', line: 'well done, little loaf. my chamber is yours.',
      pos: new THREE.Vector3(0, 0, -7.5), facing: 0, wander: 0, speed: 0, bubbleY: 2.8, build
    });
  }

  // ---- lifecycle ----
  enter({ manager }){
    this.manager = manager;
    this.input = manager.input;
    this.input.useCamera(this.camera);
    this.input.polarRange = [0.28, 0.62];
    this.input.onTap = (ndc, ray) => this._tap(ray);

    setFlag('reachedKingsChamber', true);
    flagObjective('fortress-reopened', 'reachedKingsChamber');
    this.bread.setLantern(hasFound('ghost-lamp') ? 1.0 : 0);

    this.vignette = document.createElement('div');
    this.vignette.className = 'fortress-vignette';
    document.body.appendChild(this.vignette);
    this._positionCamera();
    this._toExplore(true);
  }

  exit(){
    this._clearPanels();
    if (this.vignette){ this.vignette.remove(); this.vignette = null; }
    this.input.onTap = null;
    HUD.showEnter(null); HUD.say('');
  }

  _positionCamera(){
    this.azimuth = this.input?.azimuthGoal ?? 0;
    this.polar   = this.input?.polarGoal ?? 0.5;
    this.camTarget.set(this.bread.root.position.x * 0.5, 1.6, this.bread.root.position.z * 0.5 + 1);
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this.camera.lookAt(this.camTarget);
  }

  _toExplore(first){
    this.state = 'explore';
    this._clearPanels();
    this.bread.resetLimbs();
    this.input.onTap = (ndc, ray) => this._tap(ray);
    HUD.setSubtitle("the baker king's chamber");
    if (first){
      if (this.solved){
        HUD.say('The Baker King’s spirit lingers by the throne. His chamber is quiet now.');
      } else {
        HUD.say('A solemn chamber. Three symbols glow faintly on the floor, and a stone king slumps on the throne.');
      }
    }
    HUD.setHint('tap symbols in the right order · or speak to the king if he is awake');
    HUD.hideHintSoon(9000);
  }

  _tap(ray){
    if (this.state !== 'explore') return;
    if (this.king.root.visible && checkNpcClick(ray, this.king, this.bread)) return;

    const dir = ray.ray.direction;
    if (Math.abs(dir.y) < 1e-4) return;
    const t = -ray.ray.origin.y / dir.y;
    if (t <= 0) return;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = new THREE.Vector3(p.x, 0, p.z);
    gp.x = Math.max(-7, Math.min(7, gp.x));
    gp.z = Math.max(-8, Math.min(8, gp.z));
    HUD.hideHint();
    this.bread.walkTo(gp);
  }

  _pressSymbol(idx){
    if (this.solved) return;
    const expected = this.sequence.length;
    if (idx === expected){
      this._lightSymbol(idx);
      this.sequence.push(idx);
      HUD.say(`${SYMBOLS[idx].name} symbol glows warmly. (${this.sequence.length} / ${SYMBOLS.length})`);
      if (this.sequence.length >= SYMBOLS.length) this._solvePuzzle();
    } else {
      this._resetSymbols();
      this.sequence = [];
      HUD.say('The symbols dim. That was not the correct order.');
      this.bread.drawFace('o');
    }
  }

  _lightSymbol(idx){
    const s = this.symbolMeshes[idx];
    s.lit = true;
    s.inset.material.opacity = 0.95;
    s.base.material.color.setHex(SYMBOLS[idx].color);
  }

  _resetSymbols(){
    this.symbolMeshes.forEach((s, i) => {
      s.lit = false;
      s.inset.material.opacity = 0.25;
      s.base.material.color.setHex(0x3A3A3A);
    });
  }

  _solvePuzzle(){
    this.solved = true;
    setFlag('kingsChamberSolved', true);
    HUD.say('The symbols flare in unison. The stone king rises as a golden spirit!');
    this.bread.drawFace('yum');

    // animate the dormant body fading out
    if (this.kingBody){
      this.kingBody.traverse(o => {
        if (o.material){
          o.material.transparent = true;
          o.material.opacity = 0.25;
        }
      });
    }
    // turn on throne spotlight and reveal the king spirit
    this.throneLight.intensity = 2.5;
    this.king.root.visible = true;

    // king greets automatically
    this.king.showLine(6);
  }

  _buildAltar(){
    const g = new THREE.Group(); g.position.copy(this.altarPos); this.scene.add(g);
    box(1.8, 0.5, 1.2, 0x5A3A22, 0, 0.25, 0, g);
    box(1.6, 0.08, 1.0, 0xE8C24B, 0, 0.52, 0, g);
    const cloth = new THREE.Mesh(new THREE.PlaneGeometry(1.8, 1.2), new THREE.MeshLambertMaterial({ color: 0x9A2C46 }));
    cloth.rotation.x = -Math.PI/2; cloth.position.set(0, 0.56, 0); g.add(cloth);
  }

  _altarOffer(){
    if (!isActive('ceremony-of-toast')){
      HUD.say('A cold stone altar. It waits for a worthy offering.');
      return;
    }
    const dishes = getState().inventory.dishes || {};
    if ((dishes['royal-tart'] || 0) < 1){
      HUD.say('The altar needs a Royal Tart. Bake one at your house stove.');
      return;
    }
    // spend the dish
    dishes['royal-tart']--;
    if (dishes['royal-tart'] <= 0) delete dishes['royal-tart'];
    setFlag('performedToastCeremony', true);
    flagObjective('ceremony-of-toast', 'performedToastCeremony');
    this.bread.drawFace('yum');
    HUD.say('The Royal Tart vanishes in a warm golden light. The Golden Loaf is restored!');
    if (isActive('ceremony-of-toast') && isReady('ceremony-of-toast')){
      // already flagged by flagObjective above; turn in for reward
      turnIn('ceremony-of-toast');
      HUD.refreshCrumbs();
    }
  }

  _talkToKing(){
    if (!this.solved){
      HUD.say('The stone king is silent. The floor symbols must know the right order.');
      this.bread.drawFace('o');
      return;
    }
    this.bread.stop();
    this.bread.drawFace('grin');
    this.king.showLine(6);

    if (isActive('crown-the-crumb')){
      const need = ['relic-crown','relic-scepter','relic-gauntlet','relic-bone'];
      const missing = need.filter(id => !hasFound(id));
      if (missing.length){
        HUD.say(`The Baker King: 'Bring me the Crown, Scepter, Gauntlet, and Bread-Bone. You still need ${missing.length}.'`);
        return;
      }
      setFlag('collectedRoyalRegalia', true);
      flagObjective('crown-the-crumb', 'collectedRoyalRegalia');
      turnIn('crown-the-crumb');
      HUD.refreshCrumbs();
      HUD.say("The Baker King: 'My regalia! The Golden Loaf can be restored. Cook a Royal Tart and place it on the altar.'");
      return;
    }

    if (isActive('ceremony-of-toast') && isReady('ceremony-of-toast')){
      turnIn('ceremony-of-toast');
      HUD.refreshCrumbs();
      HUD.say("The Baker King: 'The ceremony is complete. My statue stands in Toast Town.'");
      return;
    }

    this._giveReward();
  }

  _giveReward(){
    if (getFlag('kingsRewardClaimed')){
      HUD.say('The Baker King has already given his blessing. Town prices stay kind.');
      return;
    }
    setFlag('kingsRewardClaimed', true);
    const CRUMBS = 120;
    addCrumbs(CRUMBS); HUD.refreshCrumbs();
    const first = !hasFound('relic-scepter');
    addFound('relic-scepter');
    setFlag('townDiscount', true); // permanent 10% discount hook

    this._clearPanels();
    const p = this._panel(`
      <div class="cafe-card">
        <div class="cafe-grade">👑</div>
        <h2>The Baker King</h2>
        <div class="cafe-sub">the throne acknowledges you</div>
        <p class="fortress-loot">⚜️ Royal Scepter</p>
        <p class="fortress-note">${first ? 'a relic of the old crumb-kings — added to your collection!' : 'the scepter hums with familiar warmth.'}</p>
        <p class="cafe-payout">🍞 +${CRUMBS} crumbs</p>
        <p class="fortress-note">shops across Toast Town now offer a loyal subject's discount.</p>
        <button class="cafe-btn ghost close">hail to the toast</button>
      </div>
    `);
    p.querySelector('.close').onclick = () => this._clearPanels();
  }

  _exit(){
    this.manager.goTo('fortress', { from: 'fortressThrone' });
  }

  _panel(html, bare){
    const el = document.createElement('div');
    el.className = bare ? '' : 'cafe-panel';
    el.innerHTML = html;
    document.body.appendChild(el);
    this.panels.push(el);
    return el;
  }
  _clearPanels(){ this.panels.forEach(p => p.remove()); this.panels = []; }

  // ---- loop ----
  update(dt){
    const t = performance.now() * 0.001;

    // gentle throne light pulse once solved
    if (this.solved && this.throneLight.intensity > 0){
      this.throneLight.intensity = 2.2 + Math.sin(t * 2) * 0.3;
    }

    this.king.update(dt, this.bread.root.position);
    this.bread.update(dt);

    if (this.state === 'explore'){
      let near = null, nd = Infinity;
      this.hotspots.forEach(h => {
        if (h.label.includes('speak to the Baker King') && !this.solved) return;
        const d = h.pos.distanceTo(this.bread.root.position);
        if (d < nd){ nd = d; near = h; }
      });
      if (near && nd < near.r){
        if (this.nearSpot !== near){
          this.nearSpot = near;
          HUD.showEnter(near.label, () => near.action());
        }
      } else if (this.nearSpot){ this.nearSpot = null; HUD.showEnter(null); }
    }

    this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
    this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
    this.camTarget.lerp(new THREE.Vector3(this.bread.root.position.x * 0.5, 1.6, this.bread.root.position.z * 0.5 + 1), 0.06);
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this.camera.lookAt(this.camTarget);
  }

  onResize(){
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }
}
